import { Typography } from '@remarkable/ark-web';
import { createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';

import type { FleetMemberRow } from 'src/api/endpoints/cloudEnterprise.types';
import { Tooltip } from 'src/components';
import { CopyButton } from 'src/components/CopyButton';
import { IntegrationIcon } from 'src/components/IntegrationIcon';
import { TimeSinceNow } from 'src/components/TimeSinceNow';
import { createSelectColumn } from 'src/utils/createTableSelectColumn';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { formatShortDate } from 'src/utils/formatDate';
import { sortByDate } from 'src/utils/sortByDate';
import { sortByDeviceLists } from 'src/utils/sortByDeviceLists';
import { sortByIntegrationLists } from 'src/utils/sortByIntegrationLists';

import { WarningTriangle } from '@remarkable/ark-icons';
import { capitalize } from 'src/utils/capitalize';
import { getIntegrationName } from '../utils/getIntegrationName';
import { CellDevices } from './CellDevices';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';
import type { MemberActionMenuOwnershipContext } from './MemberActionMenu';
import { MemberActionMenu } from './MemberActionMenu';
import { RoleSelector } from './RoleSelector';

const columnHelper = createColumnHelper<FleetMemberRow>();

export function createFleetTableColumns(
  ownershipContext?: MemberActionMenuOwnershipContext
) {
  return [
    createSelectColumn(columnHelper),
    columnHelper.accessor(
      (d) =>
        fleetMemberRowHelpers.getName(d) + fleetMemberRowHelpers.getEmail(d),
      {
        header: 'User',
        meta: {
          truncate: true,
          colWidth: 'w-1/5',
        },
        cell: (info) => {
          const fullName = fleetMemberRowHelpers.getName(info.row.original);
          const hasName = fleetMemberRowHelpers.hasName(info.row.original);
          const isInvitation = fleetMemberRowHelpers.isInvitation(
            info.row.original
          );

          return (
            <div className="w-full">
              {hasName && (
                <Typography variant="interface-sm" className="truncate">
                  {fullName}
                </Typography>
              )}
              <Typography
                variant={hasName ? 'interface-xs' : 'interface-sm'}
                className={clsx('mt-6 truncate', {
                  'text-text-muted': hasName,
                  italic: isInvitation,
                })}
              >
                {fleetMemberRowHelpers.getEmail(info.row.original)}
              </Typography>
            </div>
          );
        },
      }
    ),
    columnHelper.accessor((d) => capitalize(d.type), {
      header: 'Type',
      id: 'type',
    }),
    columnHelper.accessor(
      (d) => fleetMemberRowHelpers.getRoleNames(d).join(', '),
      {
        id: 'roles',
        header: 'Roles',
        meta: {
          truncate: false,
          colWidth: 'w-1/6',
          fullWidth: true,
        },
        getUniqueValues: (row) => {
          const roles = fleetMemberRowHelpers.getRoleNames(row);
          return roles.map((r) => r);
        },
        filterFn: (row, _, value) => {
          const roles = fleetMemberRowHelpers.getRoleNames(row.original);

          if (Array.isArray(value)) {
            return roles.some((r) => value.includes(r));
          }
          return false;
        },
        cell(info) {
          if (!fleetMemberRowHelpers.isInvitation(info.row.original)) {
            return <RoleSelector member={info.row.original.data} />;
          }

          return (
            <div className="flex w-full items-center justify-between gap-12">
              <span>Invited</span>
              <CopyButton
                variant="tertiary"
                size="small"
                className="inline-flex"
                value={info.row.original.data.invitationUrl}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                Copy link
              </CopyButton>
            </div>
          );
        },
      }
    ),
    columnHelper.accessor(fleetMemberRowHelpers.getLastSeenDate, {
      header: 'Last seen',
      meta: {
        colWidth: 'w-1/6',
      },
      sortingFn: (a, b) => {
        return sortByDate(
          fleetMemberRowHelpers.getLastSeenDate(a.original),
          fleetMemberRowHelpers.getLastSeenDate(b.original)
        );
      },
      cell: (info) => <TimeSinceNow date={info.getValue()} />,
    }),
    columnHelper.accessor((d) => fleetMemberRowHelpers.getStatus(d), {
      header: 'Status',
      id: 'status',
      meta: {
        colWidth: 'w-1/7',
      },
      cell: (info) => (
        <span>
          {fleetMemberRowHelpers.isMdmDeactivated(info.row.original) && (
            <span
              data-cy="mdm-deactivated-indicator"
              className="mr-8 inline-block size-8 rounded-full bg-feedback-red-500 align-middle"
            />
          )}
          {info.getValue()}
        </span>
      ),
      getUniqueValues: (row) => {
        const status = fleetMemberRowHelpers.getStatus(row);
        return [status];
      },
      filterFn: (row, _, value) => {
        if (Array.isArray(value)) {
          const status = fleetMemberRowHelpers.getStatus(row.original);
          return value.includes(status);
        }
        return false;
      },
    }),
    columnHelper.accessor(
      (d) => fleetMemberRowHelpers.getIntegrationNames(d).join(', ') || '-',
      {
        header: 'Integrations',
        id: 'integrations',
        enableHiding: true,
        meta: {
          colWidth: 'w-1/7',
        },
        sortingFn: (a, b) => {
          return sortByIntegrationLists(
            fleetMemberRowHelpers.getIntegrations(a.original),
            fleetMemberRowHelpers.getIntegrations(b.original)
          );
        },
        getUniqueValues: (row) => {
          const integrations = fleetMemberRowHelpers.getIntegrations(row);
          return integrations.map((i) => i.provider);
        },
        filterFn: (row, _, value) => {
          const integrations = fleetMemberRowHelpers.getIntegrations(
            row.original
          );

          if (Array.isArray(value)) {
            return integrations.some((i) => value.includes(i.provider ?? ''));
          }
          return false;
        },
        cell: (info) => (
          <ul className="flex items-center gap-4">
            {fleetMemberRowHelpers
              .getIntegrations(info.row.original)
              .map((integration) => (
                <li
                  className="shrink-0"
                  key={
                    (integration.provider ?? 'provider') +
                    (integration.createdAt ?? 'createdAt')
                  }
                >
                  <Tooltip
                    title={
                      <>
                        {integration.hasIssue && (
                          <WarningTriangle
                            size={20}
                            className="mr-4 inline text-feedback-orange-500"
                          />
                        )}
                        <span>
                          {getIntegrationName(integration)} (
                          {formatShortDate(integration.createdAt, {
                            showTime: true,
                            showYear: true,
                          })}
                          ){integration.hasIssue && ' - Needs reauthorization'}
                        </span>
                      </>
                    }
                  >
                    <div className="relative">
                      {integration.hasIssue && (
                        <span className="absolute top-0 right-0 size-8 rounded-full border border-neutral-light-2 bg-feedback-orange-500"></span>
                      )}
                      <IntegrationIcon
                        className="h-20"
                        provider={integration.provider}
                      />
                    </div>
                  </Tooltip>
                </li>
              ))}
          </ul>
        ),
      }
    ),
    columnHelper.accessor(
      (data) =>
        fleetMemberRowHelpers
          .getDevices(data)
          .map((d) =>
            [
              deviceHelpers.getDeviceName(d),
              deviceHelpers.getDeviceType(d),
              d.serialNumber,
            ].join('|')
          )
          .join(', ') || '-',
      {
        id: 'devices',
        header: 'Devices',
        meta: {
          colWidth: 'w-1/7',
          fullWidth: false,
          truncate: true,
        },
        sortingFn: (a, b) => {
          return sortByDeviceLists(
            fleetMemberRowHelpers.getDevices(a.original),
            fleetMemberRowHelpers.getDevices(b.original)
          );
        },
        cell: (info) => (
          <CellDevices
            // We need the key to force a re-render when the column is sorted so
            // that the Ellipsis component can recalculate the overflow
            key={info.column.getIsSorted().valueOf().toString()}
            row={info.row.original}
          />
        ),
        getUniqueValues: (row) => {
          const devices = fleetMemberRowHelpers.getDevices(row);
          return devices.map(deviceHelpers.getDeviceName);
        },
        filterFn: (row, _, value) => {
          const devices = fleetMemberRowHelpers.getDevices(row.original);

          if (Array.isArray(value)) {
            const matchesDeviceId = devices.some((d) =>
              value.includes(d.serialNumber ?? '')
            );
            const matchesDeviceName = devices.some((d) =>
              value.includes(deviceHelpers.getDeviceName(d))
            );

            return matchesDeviceId || matchesDeviceName;
          }
          return false;
        },
      }
    ),
    columnHelper.accessor(
      (data) =>
        fleetMemberRowHelpers
          .getDevices(data)
          .map((d) => d.softwareVersion)
          .join(', '),
      {
        id: 'device-software-versions',
        header: 'Software versions',
        getUniqueValues: (row) => {
          const devices = fleetMemberRowHelpers.getDevices(row);
          return devices.map((d) => {
            return d.softwareVersion?.split('.').slice(0, 3).join('.');
          });
        },
        filterFn: (row, _, value) => {
          const devices = fleetMemberRowHelpers.getDevices(row.original);

          if (Array.isArray(value)) {
            return devices.some((d) => value.includes(d.softwareVersion ?? ''));
          }
          return false;
        },
      }
    ),
    columnHelper.accessor((d) => fleetMemberRowHelpers.getBetaStatus(d), {
      id: 'beta',
      header: 'Beta',
      filterFn: (row, _, value) => {
        if (Array.isArray(value)) {
          return value.includes(
            fleetMemberRowHelpers.getBetaStatus(row.original)
          );
        }
        return false;
      },
      getUniqueValues: (row) => {
        return [fleetMemberRowHelpers.getBetaStatus(row)];
      },
    }),
    columnHelper.display({
      header: 'Edit',
      meta: {
        align: 'center',
        colWidth: 'w-[50px]',
        truncate: false,
      },
      cell: ({ row }) =>
        fleetMemberRowHelpers.isUser(row.original) ? (
          <MemberActionMenu
            member={row.original.data}
            ownershipContext={ownershipContext}
          />
        ) : null,
    }),
  ];
}
